import { useEffect, useRef, useState } from "react";
import { loadTurnstile, loadTurnstileSiteKey, TURNSTILE_MESSAGES, TurnstileError, type TurnstileFailure } from "../lib/turnstile";

export function TurnstileChallenge({ onToken }: { onToken: (token: string | null) => void }) {
	const host = useRef<HTMLDivElement>(null);
	const [attempt, setAttempt] = useState(0);
	const [message, setMessage] = useState("사람인지 확인하는 도구를 불러오고 있어요.");
	const [failure, setFailure] = useState<TurnstileFailure | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: an explicit retry starts a fresh check without discarding the draft.
	useEffect(() => {
		const controller = new AbortController();
		let widgetId: string | null = null;
		let cancelled = false;
		onToken(null);
		setFailure(null);
		setMessage("사람인지 확인하는 도구를 불러오고 있어요.");
		const fail = (reason: TurnstileFailure) => {
			if (cancelled) return;
			onToken(null);
			setFailure(reason);
			setMessage(TURNSTILE_MESSAGES[reason]);
		};
		void (async () => {
			const sitekey = await loadTurnstileSiteKey(controller.signal);
			if (cancelled) return;
			await loadTurnstile();
			if (cancelled || !host.current) return;
			if (!window.turnstile) throw new TurnstileError("script");
			setMessage("확인을 마치면 한 줄 공개하기 버튼을 눌러 주세요.");
			widgetId = window.turnstile.render(host.current, {
				sitekey, language: "ko", retry: "never",
				callback: (token) => {
					if (cancelled) return;
					onToken(token);
					setFailure(null);
					setMessage("확인을 마쳤어요. 한 줄 공개하기 버튼을 눌러 주세요.");
				},
				"expired-callback": () => fail("expired"),
				"timeout-callback": () => fail("timeout"),
				"error-callback": (code) => fail(["110100", "110110", "110200"].includes(code) ? "config_missing" : "challenge"),
			});
		})().catch((error: unknown) => fail(error instanceof TurnstileError ? error.reason : "challenge"));
		return () => {
			cancelled = true;
			controller.abort();
			if (widgetId !== null) window.turnstile?.remove(widgetId);
		};
	}, [attempt, onToken]);

	return <div className="card stack" role="group" aria-label="사람인지 확인">
		<strong>한 번만 확인해 주세요.</strong><div ref={host} />
		<p className="micro" role={failure ? "alert" : "status"}>{message}</p>
		{failure ? <button type="button" className="button" onClick={() => setAttempt((value) => value + 1)}>사람 확인 다시 시도하기</button> : null}
	</div>;
}
